import CTNCalendar from '@ctn/ui/components/ctn-calendar';
import CTNModal from '@ctn/ui/components/ctn-modal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { type CTNModalProps } from '@ctn/ui/types/ctn-modal';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { addDays, addYears, format } from 'date-fns';
import { useEffect, useState, type ReactElement } from 'react';
import { useForm, useFormContext, type Resolver } from 'react-hook-form';
import * as yup from 'yup';

import { useGlobalContext } from '@/context/global.context';
import useApiData from '@/hooks/useApiData';
import { postAmpliacionVencimiento } from '@/services/tramite.service';

import { FORMAT_DATE } from '@/constants/date.constants';
import ERROR_MESSAGE from '@/constants/errors.constants';
import { commonCalendar } from '@/helpers/calendar';
import { notificationsConfig } from '@/helpers/notifications';
import { peekValidation } from '@/helpers/validations';
import { type FormTramiteValues } from '@/interfaces/model/don.interface';


type ModalParams = {
	readonly isOpen: boolean;
	readonly closeModal: () => void;
	readonly onSave?: () => void;
};

type Form = {
	fechaVencimiento: Date;
	fechaVencimientoNueva: Date;
};

const schema = yup.object().shape({
	fechaVencimientoNueva: yup.string().required(ERROR_MESSAGE.required),
});

export const resolver = (): Resolver<any, string> => yupResolver(schema);

const ModalCambiarVencimiento = ({ isOpen, closeModal, onSave }: ModalParams): ReactElement => {
	const { user } = useGlobalContext();
	const { getValues } = useFormContext<FormTramiteValues>();
	const [hasMissingRequired, setHasMissingRequired] = useState(true);
	const [isHidden, setIsHidden] = useState(false);
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const { triggerFetch, loading } = useApiData({ loading: ['ampliacionVencimiento'] as const });

	const {
		control,
		formState: { errors },
		watch,
		reset,
		handleSubmit,
	} = useForm<Form>({
		mode: 'onSubmit',
		resolver: resolver(),
	});

	const formData = watch();

	const handleSave = async ({ fechaVencimientoNueva }: Form) => {
		if (!user) return;
		const [nifEmisor, idTramite] = getValues(['nifEmisor', 'id']);

		const payload = {
			vigencia: format(fechaVencimientoNueva, FORMAT_DATE.BASIC_DASH),
			nifEmisor,
			idTramite,
			tipoSector: user?.tipoSectorEmisor,
		};

		const { code } = await triggerFetch({
			fetch: postAmpliacionVencimiento,
			payload,
			name: 'ampliacionVencimiento',
			error: (
				<>
					<p>Se ha producido un error en el cambio de fecha de vencimiento.</p>
					<p> Por favor, vuelva a intentarlo.</p>
				</>
			),
			onCloseError: () => setIsHidden(false)
		});

		if (code !== 200) {
			setIsHidden(true);
			return;
		}

		openNotification(
			notificationTemplate.successMessage(
				'Cambio de fecha',
				'Se ha realizado el cambio de fecha de vencimiento correctamente.',
			),
		);

		if (onSave) {
			onSave();
		}

		closeModal();
	};

	const actionButtons: CTNModalProps['actionButtons'] = [
		{
			key: 'tertiary',
			mode: 'tertiary',
			label: 'Cerrar',
			size: 'size-40',
			minWidth: '140',
			onClick: closeModal,
		},
		{
			key: 'primary',
			mode: 'primary',
			disabled: hasMissingRequired || loading.ampliacionVencimiento,
			label: 'Confirmar',
			minWidth: '140',
			size: 'size-40',
			onClick() {
				handleSubmit(handleSave)();
			},
		},
	];

	useEffect(() => {
		const check = async () => {
			const hasAllRequiredFields = await peekValidation({
				schema,
				data: formData,
				fields: Object.keys(formData) as Array<keyof Form>,
			});

			setHasMissingRequired(!hasAllRequiredFields);
		};

		check();
	}, [formData, schema]);

	useEffect(() => {
		const fechaVencimiento = getValues('fechaVencimiento');
		setHasMissingRequired(true);
		reset({ fechaVencimiento });
	}, [isOpen]);

	return (
		<CTNModal
			autoHeight
			closeable
			type="basic"
			className={classNames('c-pnp-modal', { 'u-hidden': isHidden })}
			title="Nueva fecha de vencimiento"
			size="md"
			alignButtons="right"
			actionButtons={actionButtons}
			show={isOpen}
			content={
				<div className="o-grid ">
					<div className="o-grid__row">
						<CTNCalendar
							{...commonCalendar}
							readOnly
							name="fechaVencimiento"
							classNameField="o-grid__col-12 o-grid__col-6@portrait"
							label="Fecha de vencimiento actual"
							control={control}
						/>

						<CTNCalendar
							{...commonCalendar}
							required
							monthsShown={1}
							classNameField="o-grid__col-12 o-grid__col-6@portrait"
							name="fechaVencimientoNueva"
							label="Nueva fecha de vencimiento"
							todayButton=""
							appendTo="body"
							minDate={addDays(new Date(), 1)}
							maxDate={addYears(new Date(), 2)}
							control={control}
							error={errors.fechaVencimiento?.message}
						/>
					</div>
				</div>
			}
			onClose={closeModal}
		/>
	);
};

export default ModalCambiarVencimiento;
